import { useEffect, useState } from "react";
import AdminShell from "@/components/admin/AdminShell";
import api, { formatApiError } from "@/lib/api";
import { Sparkles, UserPlus, Camera, Upload, CheckCircle2, Clock, RotateCcw, Trash2, FileText, ExternalLink } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CertSlot({ childId, sessionNumber, overrides, onSaved, uploading, setUploadingSlot }) {
  const current = overrides?.[`${childId}:${sessionNumber}`]?.image_url || "";
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr(""); setUploadingSlot(sessionNumber);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/thrive-story/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const publicUrl = `${BACKEND_URL}/api/public-files/${data.path}`;
      // Save as a content override keyed by {child_id}:{session_number}
      await api.put("/admin/content", {
        content_type: "thrive_story_cert",
        key: `${childId}:${sessionNumber}`,
        field: "image_url",
        value: publicUrl,
      });
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); setUploadingSlot(null); }
  };
  const clear = async () => {
    setBusy(true); setErr("");
    try {
      await api.put("/admin/content", {
        content_type: "thrive_story_cert",
        key: `${childId}:${sessionNumber}`,
        field: "image_url",
        value: "",
      });
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden" data-testid={`cert-slot-${sessionNumber}`}>
      <div className="aspect-[1.4] bg-[#0F1020] flex items-center justify-center">
        {current ? (
          <img src={current} alt={`Session ${sessionNumber}`} className="w-full h-full object-contain" />
        ) : (
          <div className="text-xs text-slate-500 flex flex-col items-center gap-1">
            <Camera size={16} /> Not uploaded
          </div>
        )}
      </div>
      <div className="px-2.5 py-2 flex items-center justify-between">
        <div className="text-xs font-semibold text-white">Session {sessionNumber}</div>
        <label className="text-xs text-amber-300 hover:underline cursor-pointer inline-flex items-center gap-1">
          <Upload size={11} /> {busy ? "…" : current ? "Replace" : "Upload"}
          <input type="file" accept="image/*" className="hidden" data-testid={`cert-upload-${sessionNumber}`} disabled={busy || uploading} onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
        </label>
      </div>
      {current && (
        <button onClick={clear} className="w-full text-[10px] text-slate-500 hover:text-rose-300 py-1 border-t border-white/5" data-testid={`cert-clear-${sessionNumber}`}>Remove</button>
      )}
      {err && <div className="px-2 pb-2 text-[10px] text-rose-400">{err}</div>}
    </div>
  );
}

function FamilyCard({ family, overrides, onReload }) {
  const [grantBusy, setGrantBusy] = useState(false);
  const [slotBusy, setSlotBusy] = useState(false);
  const revokeBonus = async () => {
    if (!window.confirm(`Revoke Thrive Story bonus for ${family.email}?`)) return;
    setGrantBusy(true);
    try { await api.post("/admin/thrive-story/grant", { email: family.email, revoke: true }); onReload(); }
    finally { setGrantBusy(false); }
  };
  const adjustSlots = async (delta) => {
    setSlotBusy(true);
    try {
      const next = Math.max(0, (family.ts_paid_slots || 0) + delta);
      await api.post(`/admin/parent/${family.parent_id}/thrive-story-slots`, { paid_slots: next });
      onReload();
    } finally { setSlotBusy(false); }
  };
  const used = family.ts_used || 0;
  const allowed = family.ts_allowed || 1;
  const remaining = allowed - used;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid={`family-${family.parent_id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-base font-semibold text-white">{family.name || family.email}</div>
          <div className="text-xs text-slate-400">{family.email}</div>
        </div>
        <button onClick={revokeBonus} disabled={grantBusy} className="text-xs text-slate-400 hover:text-rose-300 inline-flex items-center gap-1" data-testid={`revoke-${family.parent_id}`}>
          <RotateCcw size={11} /> Revoke bonus
        </button>
      </div>
      <div className="mt-3 flex items-center gap-3 flex-wrap text-xs">
        <div className={`px-3 py-1.5 rounded-full font-semibold ${remaining > 0 ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid={`slots-${family.parent_id}`}>
          Slots: {used}/{allowed} used · {remaining} available
        </div>
        <div className="inline-flex items-center gap-1 text-slate-400">
          <span className="text-[11px] uppercase tracking-widest">Paid add-on slots</span>
          <button onClick={() => adjustSlots(-1)} disabled={slotBusy || (family.ts_paid_slots || 0) <= 0} className="w-6 h-6 rounded bg-white/10 hover:bg-white/20 text-white disabled:opacity-30" data-testid={`slots-minus-${family.parent_id}`}>−</button>
          <span className="w-6 text-center text-white font-mono">{family.ts_paid_slots || 0}</span>
          <button onClick={() => adjustSlots(1)} disabled={slotBusy} className="w-6 h-6 rounded bg-white/10 hover:bg-white/20 text-white" data-testid={`slots-plus-${family.parent_id}`}>+</button>
        </div>
      </div>
      {family.children.length === 0 ? (
        <div className="mt-3 text-xs text-slate-400 italic">Bonus granted — waiting for the parent to pick "My Thrive Story" during enrollment.</div>
      ) : family.children.map((c) => (
        <ChildStoryEditor key={c.id} child={c} overrides={overrides} onReload={onReload} />
      ))}
    </div>
  );
}

function ChildStoryEditor({ child, overrides, onReload }) {
  const [color, setColor] = useState(child.favorite_color || "#8B5E7A");
  const [companionPath, setCompanionPath] = useState(child.companion_photo_path || "");
  const [companionName, setCompanionName] = useState(child.companion_name || "");
  const [encouragements, setEncouragements] = useState(() => {
    const seed = Array.isArray(child.encouragements) ? child.encouragements : [];
    return seed.length > 0 ? seed : [""];
  });
  const [pdfPath, setPdfPath] = useState(child.thrive_world_pdf_path || "");
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [ready, setReady] = useState(!!child.story_assets_ready);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploadingCompanion] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [err, setErr] = useState("");

  const save = async (patch = {}) => {
    setSaving(true); setErr("");
    try {
      // Trim empty rows before persisting so the parent-facing list stays clean.
      const cleanedEnc = encouragements.map((e) => (e || "").trim()).filter(Boolean).slice(0, 10);
      const body = {
        favorite_color: color,
        companion_photo_path: companionPath,
        companion_name: companionName.trim(),
        encouragements: cleanedEnc,
        thrive_world_pdf_path: pdfPath,
        story_assets_ready: ready,
        ...patch,
      };
      await api.post(`/admin/thrive-story/${child.id}`, body);
      onReload();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const addEncouragement = () => {
    if (encouragements.length >= 10) return;
    setEncouragements([...encouragements, ""]);
  };
  const removeEncouragement = (i) => {
    const next = encouragements.filter((_, idx) => idx !== i);
    setEncouragements(next.length ? next : [""]);
  };
  const updateEncouragement = (i, v) => {
    setEncouragements(encouragements.map((e, idx) => (idx === i ? v : e)));
  };
  const uploadPdf = async (file) => {
    if (!file) return;
    setUploadingPdf(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/thrive-story/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPdfPath(data.path);
      await save({ thrive_world_pdf_path: data.path });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setUploadingPdf(false); }
  };
  const clearPdf = async () => {
    if (!window.confirm("Remove the Thrive World PDF for this child?")) return;
    setPdfPath("");
    await save({ thrive_world_pdf_path: "" });
  };
  const uploadCompanion = async (file) => {
    if (!file) return;
    setUploadingCompanion(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/thrive-story/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCompanionPath(data.path);
      await save({ companion_photo_path: data.path });
    } catch (e) { setErr(formatApiError(e)); }
    finally { setUploadingCompanion(false); }
  };
  const uploadZip = async (file) => {
    if (!file) return;
    setBulkBusy(true); setErr(""); setBulkResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/thrive-story/${child.id}/upload-cert-zip`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkResult(data);
      onReload();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };

  const companionUrl = companionPath ? `${BACKEND_URL}/api/public-files/${companionPath}` : null;

  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-[#0F1020]/60 p-4" data-testid={`child-editor-${child.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full overflow-hidden border-2" style={{ borderColor: color, background: "#FFFEF9" }}>
            {companionUrl ? (
              <img src={companionUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs">—</div>
            )}
          </div>
          <div>
            <div className="font-semibold text-white">{child.name}{child.age ? ` · age ${child.age}` : ""}</div>
            <div className="text-xs text-slate-400">Session {child.current_session} of 12</div>
          </div>
        </div>
        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold ${ready ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`} data-testid={`status-${child.id}`}>
          {ready ? <><CheckCircle2 size={12} /> Published</> : <><Clock size={12} /> Pending</>}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold mb-1">Favorite color (hex)</div>
          <div className="flex items-center gap-2">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-10 h-10 rounded cursor-pointer bg-transparent border border-white/10" data-testid={`color-picker-${child.id}`} />
            <input type="text" value={color} onChange={(e) => setColor(e.target.value)} className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm font-mono" data-testid={`color-text-${child.id}`} />
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold mb-1">Adventure companion photo</div>
          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white cursor-pointer hover:bg-white/10">
            <Camera size={14} /> {uploading ? "Uploading…" : companionPath ? "Replace photo" : "Upload photo"}
            <input type="file" accept="image/*" className="hidden" data-testid={`companion-upload-${child.id}`} disabled={uploading} onChange={(e) => e.target.files[0] && uploadCompanion(e.target.files[0])} />
          </label>
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold mb-1">Companion name (what the kid calls them)</div>
        <input
          type="text"
          value={companionName}
          onChange={(e) => setCompanionName(e.target.value)}
          placeholder="Shelly"
          maxLength={40}
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
          data-testid={`companion-name-${child.id}`}
        />
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">Encouragements from {companionName.trim() || "the companion"} (up to 10)</div>
            <div className="text-[11px] text-slate-400 mt-0.5">Short messages in the companion's voice. The child cycles through them in order — no repeats until the whole list is seen.</div>
          </div>
          <button
            onClick={addEncouragement}
            disabled={encouragements.length >= 10}
            className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-300/40 text-amber-300 hover:bg-white/5 disabled:opacity-40"
            data-testid={`add-encouragement-${child.id}`}
          >
            + Add another
          </button>
        </div>
        <div className="space-y-2">
          {encouragements.map((e, i) => (
            <div key={i} className="flex items-start gap-2" data-testid={`encouragement-row-${child.id}-${i}`}>
              <div className="w-6 h-6 rounded-full bg-amber-500/15 text-amber-300 text-[10px] font-mono flex items-center justify-center shrink-0 mt-2">{i + 1}</div>
              <textarea
                value={e}
                onChange={(ev) => updateEncouragement(i, ev.target.value)}
                rows={2}
                maxLength={240}
                placeholder={i === 0 ? "You're doing amazing — take a deep nose breath with me and we'll try one more time." : "Another encouraging message…"}
                className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm resize-none"
              />
              <button
                onClick={() => removeEncouragement(i)}
                className="w-8 h-8 rounded flex items-center justify-center text-slate-500 hover:text-rose-300 hover:bg-white/5 mt-1"
                data-testid={`remove-encouragement-${child.id}-${i}`}
                aria-label={`Remove encouragement ${i + 1}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold mb-2">Full Thrive World PDF (28-page personalized story)</div>
        {pdfPath ? (
          <div className="flex items-center gap-3 flex-wrap">
            <a href={`${BACKEND_URL}/api/public-files/${pdfPath}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 text-sm hover:bg-emerald-500/25" data-testid={`pdf-view-${child.id}`}>
              <ExternalLink size={13} /> View current PDF
            </a>
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white cursor-pointer hover:bg-white/10">
              <Upload size={13} /> {uploadingPdf ? "Uploading…" : "Replace PDF"}
              <input type="file" accept="application/pdf,.pdf" className="hidden" disabled={uploadingPdf} data-testid={`pdf-upload-${child.id}`} onChange={(e) => e.target.files[0] && uploadPdf(e.target.files[0])} />
            </label>
            <button onClick={clearPdf} className="text-xs text-slate-500 hover:text-rose-300" data-testid={`pdf-remove-${child.id}`}>Remove</button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white/5 border border-dashed border-white/20 text-sm text-slate-300 cursor-pointer hover:bg-white/10">
            <FileText size={14} /> {uploadingPdf ? "Uploading…" : "Upload Thrive World PDF"}
            <input type="file" accept="application/pdf,.pdf" className="hidden" disabled={uploadingPdf} data-testid={`pdf-upload-${child.id}`} onChange={(e) => e.target.files[0] && uploadPdf(e.target.files[0])} />
          </label>
        )}
        <div className="text-[11px] text-slate-500 mt-1.5">Shown to the parent on their child's dashboard as "View Full Thrive World" — opens in a new tab and can be downloaded / printed.</div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">Personalized session certificates (12 total)</div>
          <label className={`text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border cursor-pointer ${bulkBusy ? "opacity-60" : "hover:bg-white/5"} border-amber-300/40 text-amber-300`} data-testid={`bulk-zip-${child.id}`}>
            <Upload size={11} /> {bulkBusy ? "Extracting zip…" : "Bulk upload all 12 (.zip)"}
            <input type="file" accept=".zip,application/zip" className="hidden" disabled={bulkBusy} onChange={(e) => e.target.files[0] && uploadZip(e.target.files[0])} />
          </label>
        </div>
        {bulkResult && (
          <div className="mb-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-200" data-testid={`bulk-result-${child.id}`}>
            <div className="font-semibold text-emerald-300 mb-1">Uploaded sessions: {bulkResult.matched_sessions.join(", ") || "none"}</div>
            {bulkResult.skipped_files?.length > 0 && (
              <div className="text-slate-400 text-[11px]">Skipped (filename didn't contain 1–12): {bulkResult.skipped_files.slice(0,5).join(", ")}{bulkResult.skipped_files.length > 5 ? ` +${bulkResult.skipped_files.length - 5} more` : ""}</div>
            )}
            <div className="text-slate-400 text-[11px] mt-1">Tip: name files like "session-1.png", "01.png", or "Session 3 - Indie.png" — anything with the number works.</div>
          </div>
        )}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {[1,2,3,4,5,6,7,8,9,10,11,12].map((n) => (
            <CertSlot key={n} childId={child.id} sessionNumber={n} overrides={overrides} onSaved={onReload} uploading={uploadingSlot === n} setUploadingSlot={setUploadingSlot} />
          ))}
        </div>
      </div>

      {err && <div className="mt-3 text-xs text-rose-300">{err}</div>}

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <button onClick={() => save()} disabled={saving} className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm text-white font-semibold" data-testid={`save-${child.id}`}>
          {saving ? "Saving…" : "Save color"}
        </button>
        <button onClick={() => { setReady(!ready); save({ story_assets_ready: !ready }); }} disabled={saving} className={`px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 ${ready ? "bg-amber-500/20 text-amber-300 hover:bg-amber-500/30" : "bg-emerald-500 text-[#0F1020] hover:bg-emerald-400"}`} data-testid={`toggle-publish-${child.id}`}>
          <Sparkles size={14} /> {ready ? "Unpublish" : "Publish story"}
        </button>
      </div>
    </div>
  );
}

export default function AdminThriveStories() {
  const [data, setData] = useState({ families: [], orphaned_children: [] });
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);
  const [grantErr, setGrantErr] = useState("");
  const [grantOk, setGrantOk] = useState("");

  const reload = () => {
    setLoading(true);
    Promise.all([api.get("/admin/thrive-stories"), api.get("/content/overrides")])
      .then(([ts, ov]) => {
        setData(ts.data || { families: [], orphaned_children: [] });
        setOverrides(ov.data?.thrive_story_cert || {});
      })
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const grant = async () => {
    setGrantBusy(true); setGrantErr(""); setGrantOk("");
    try {
      await api.post("/admin/thrive-story/grant", { email: grantEmail.trim() });
      setGrantOk(`Bonus granted to ${grantEmail}. They'll see the "My Thrive Story" tribe card next time they enroll a child.`);
      setGrantEmail("");
      reload();
    } catch (e) { setGrantErr(formatApiError(e)); }
    finally { setGrantBusy(false); }
  };

  return (
    <AdminShell title="My Thrive Story" subtitle="Bonus · personalized tribe" right={null}>
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <UserPlus size={14} className="text-amber-300" />
          <div className="font-semibold text-white">Grant the Thrive Story bonus</div>
        </div>
        <p className="text-xs text-slate-400 mb-3">Enter a parent's email. If they already have an account, the "My Thrive Story" option will appear in their next enrollment. If they don't yet, the bonus activates the moment they register.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="parent@email.com" className="flex-1 min-w-[200px] px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" data-testid="thrive-grant-email" />
          <button onClick={grant} disabled={!grantEmail.trim() || grantBusy} className="px-5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0F1020] text-sm font-semibold disabled:opacity-50" data-testid="thrive-grant-submit">
            {grantBusy ? "Granting…" : "Grant bonus"}
          </button>
        </div>
        {grantErr && <div className="mt-2 text-xs text-rose-300" data-testid="thrive-grant-err">{grantErr}</div>}
        {grantOk && <div className="mt-2 text-xs text-emerald-300" data-testid="thrive-grant-ok">{grantOk}</div>}
      </div>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : data.families.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 bg-white/5 p-10 text-center text-slate-400">
          <Sparkles size={28} className="mx-auto mb-3 text-amber-300" />
          No families have the Thrive Story bonus yet. Grant it above to get started.
        </div>
      ) : (
        <div className="space-y-4">
          {data.families.map((f) => <FamilyCard key={f.parent_id} family={f} overrides={overrides} onReload={reload} />)}
        </div>
      )}
    </AdminShell>
  );
}
